// combatpro/runtime/useEncounterGenerator.js
//
// Combat PRO - Random & Wave Encounter generator hook (shared component/state/
// actions layer, spec 6: the dashboard sections and the dedicated pages use the
// SAME underlying generator; generator logic is not duplicated). It owns the
// generator control state for one family, resolves the source pool (official +
// custom via MonsterSelectionContext), runs the pure encounterGenEngine, and
// commits the result through the CombatContext generation handlers. No engine
// logic lives here - only orchestration. No optional chaining. No em dash.

import { useState, useContext } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import { MonsterSelectionContext } from '../contexts/MonsterSelectionContext';
import { familyArrayKey, activeFamilyOf } from '../encounter/encounterStoreEngine';
import {
  generateRandom as engineRandom, generateWave as engineWave,
  summarizeRandom, pickSignature, RANDOM_QUICK_PRESETS, WAVE_QUICK_PRESETS
} from '../encounter/encounterGenEngine';

function defaultRandomSettings() {
  return { level: 5, useParty: false, difficulty: 'medium', count: 4, creatureType: 'any', environment: 'any', source: 'all', composition: 'random' };
}
function defaultWaveSettings() {
  return { level: 5, useParty: false, difficulty: 'medium', totalMonsters: 8, creatureType: 'any', environment: 'any', source: 'all', waves: '3', style: 'standard' };
}

function readOfficial() {
  return (typeof window !== 'undefined' && Array.isArray(window.VI_MONSTERS_DB)) ? window.VI_MONSTERS_DB : [];
}
function isFav(m) { return !!(m && (m.favourite || m.favorite || m.isFavourite)); }

function failureMessage(res) {
  if (!res) { return 'NO VALID ENCOUNTER FOR THESE SETTINGS'; }
  if (res.error === 'NO_VALID_MONSTERS') {
    return 'NO VALID MONSTERS FOR THESE SETTINGS - try relaxing the creature type or source.';
  }
  return 'NO VALID ENCOUNTER FOR THESE SETTINGS - try a different count or difficulty.';
}

export function useEncounterGenerator(family) {
  var isWave = (family === 'wave');
  var ctx = useContext(CombatContext);
  var sel = useContext(MonsterSelectionContext);
  var settingsHook = useState(isWave ? defaultWaveSettings() : defaultRandomSettings());
  var settings = settingsHook[0];
  var setSettings = settingsHook[1];
  var failHook = useState('');
  var failure = failHook[0];
  var setFailure = failHook[1];

  var customPool = (sel && Array.isArray(sel.customPool)) ? sel.customPool : [];
  var hasCustom = customPool.length > 0;
  var hasFavourites = customPool.some(isFav);

  function setField(key, value) {
    setSettings(function (prev) { var n = Object.assign({}, prev); n[key] = value; return n; });
    setFailure('');
  }
  function applyPreset(id) {
    var list = isWave ? WAVE_QUICK_PRESETS : RANDOM_QUICK_PRESETS;
    var preset = null;
    for (var i = 0; i < list.length; i++) { if (list[i].id === id) { preset = list[i]; break; } }
    if (!preset) { return; }
    setSettings(function (prev) { return Object.assign({}, prev, preset.values); });
    setFailure('');
  }

  function resolvePool(source) {
    var official = readOfficial();
    if (source === 'official') { return official; }
    if (source === 'mine') { return customPool; }
    if (source === 'favourites') { return customPool.filter(isFav); }
    return official.concat(customPool);
  }

  function activePartyLevel() {
    var store = ctx.state.encounterStore;
    if (!store) { return 0; }
    var arr = store[familyArrayKey(activeFamilyOf(store))] || store.encounters;
    var enc = arr ? arr[store.activeIndex] : null;
    var pl = (enc && enc.meta) ? parseInt(enc.meta.partyLevel, 10) : NaN;
    return isNaN(pl) ? 0 : pl;
  }
  function resolveLevel(s) {
    if (s.useParty) { var pl = activePartyLevel(); if (pl >= 1 && pl <= 20) { return pl; } }
    return s.level;
  }
  function partySize() {
    var players = ctx.state.players || [];
    return players.length > 0 ? players.length : 4;
  }
  function slotGen(idx) {
    var store = ctx.state.encounterStore;
    if (!store) { return null; }
    var arr = store[familyArrayKey(isWave ? 'wave' : 'random')];
    var slot = arr ? arr[idx] : null;
    return slot ? slot.gen : null;
  }

  function generate(idx) {
    setFailure('');
    var s = settings;
    var pool = resolvePool(s.source);
    var level = resolveLevel(s);
    var prev = slotGen(idx);
    if (isWave) {
      var wres = engineWave({
        pool: pool, level: level, partySize: partySize(), difficulty: s.difficulty,
        totalMonsters: s.totalMonsters, waves: s.waves, style: s.style, creatureType: s.creatureType
      });
      if (!wres.ok) { setFailure(failureMessage(wres)); return false; }
      var wflat = [];
      wres.waves.forEach(function (w) { w.monsters.forEach(function (m) { wflat.push(m); }); });
      var wsig = pickSignature(wflat);
      ctx.commitGeneratedWave(idx, {
        waves: wres.waves, events: wres.events,
        settings: { level: level, difficulty: wres.difficulty, totalMonsters: wres.totalMonsters, creatureType: s.creatureType, environment: s.environment, source: s.source, waves: s.waves, style: wres.style, useParty: s.useParty },
        result: { summary: wres.summary, headline: wres.summary, band: wres.difficulty, waveSummary: wres.waveSummary, waveCount: wres.waveCount, style: wres.style, signature: wsig }
      });
      return true;
    }
    var res = engineRandom({
      pool: pool, level: level, partySize: partySize(), difficulty: s.difficulty,
      count: s.count, creatureType: s.creatureType, composition: s.composition,
      avoidSignature: (prev && prev.signature) ? prev.signature : ''
    });
    if (!res.ok) { setFailure(failureMessage(res)); return false; }
    var sig = pickSignature(res.monsters);
    var headline = summarizeRandom(res.difficulty, res.level, res.count, res.composition);
    ctx.commitGeneratedRandom(idx, {
      monsters: res.monsters,
      settings: { level: level, difficulty: res.difficulty, count: res.count, creatureType: s.creatureType, environment: s.environment, source: s.source, composition: res.composition, useParty: s.useParty },
      result: { summary: res.summary, headline: headline, band: res.band, signature: sig }
    });
    return true;
  }

  return {
    family: family, isWave: isWave, settings: settings, setField: setField,
    applyPreset: applyPreset, generate: generate, failure: failure, clearFailure: function () { setFailure(''); },
    hasCustom: hasCustom, hasFavourites: hasFavourites, ctx: ctx
  };
}
